import React, { ReactElement } from 'react';
import { gql } from '@apollo/client';
import { GetServerSidePropsContext } from 'next';
import {
  GetCommunityProjectPageQuery,
  GetCommunityProjectPageQueryVariables,
} from 'generated/graphql';
import { ContentPadding } from '@components/ContentPadding';
import { PageHeader } from '@components/Text';
import Layout from '@components/Layout';
import { getClient } from 'db/contentAPI';
import Link from 'next/link';
import { routes } from '@utils/routes';
import { Media } from '@components/Media';
import { COMMUNITY_PROJECTS_PAGE_ID } from 'logic/pages';

export default function CommissionTier({
  data,
}: {
  data: GetCommunityProjectPageQuery;
}): ReactElement {
  const projects = data?.communityProjectsCollection.items ?? [];
  return (
    <Layout
      head={{
        title: 'Community Projects',
      }}
    >
      <ContentPadding>
        <PageHeader className="mb-2">Community Projects</PageHeader>
        <div className="sm:grid sm:grid-cols-2 gap-y-28 text-black mb-8">
          <div className="text-left w-full">
            {data?.communityProjectsPage.description}
          </div>
          <div className="container h-full mx-0 sm:relative"></div>
        </div>
      </ContentPadding>
      <ContentPadding className="mt-6">
        <div className="text-3xl font-semibold mb-2">Projects</div>
        <div className="sm:py-15 pt-4 pb-20">
          <div className="flex justify-around flex-col">
            {projects.map((item) => (
              <div className="w-full flex mb-16" key={item.slug}>
                {item.image?.url && (
                  <div className="flex-1 relative mr-6 sm:mr-12">
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <Media
                      className="w-6/12 object-contain object-left"
                      height="100%"
                      width="100%"
                      layout="fill"
                      src={item.image.url}
                      alt=""
                    />
                  </div>
                )}
                <div className="flex flex-col justify-end w-6/12 py-20">
                  <h2 className="text-left">{item.name}</h2>
                  <p>{item.description}</p>
                  <Link href={routes.communityProjectDetails(item.slug)}>
                    <a className="hover:text-gray-600 underline mt-6">
                      View Project
                    </a>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </ContentPadding>
    </Layout>
  );
}

const queryCommunityProjectPage = gql`
  query GetCommunityProjectPage($preview: Boolean) {
    communityProjectsPage(id: "${COMMUNITY_PROJECTS_PAGE_ID}", preview: $preview) {
      description
    }
    communityProjectsCollection(order: order_ASC) {
      items {
        name
        slug
        description
        image {
          url
        }
      }
    }
  }
`;

export async function getServerSideProps({
  res,
  preview,
  previewData,
}: GetServerSidePropsContext) {
  const { data, error } = await getClient(
    (previewData as { api: string })?.api
  ).query<GetCommunityProjectPageQuery, GetCommunityProjectPageQueryVariables>({
    query: queryCommunityProjectPage,
    variables: {
      preview,
    },
  });

  if (!preview && !error) {
    res.setHeader(
      'Cache-Control',
      'public, s-maxage=60, stale-while-revalidate=59'
    );
  }
  console.log(error);
  return {
    props: {
      data,
      preview: preview ?? false,
    },
  };
}
